export type ComparisonDataSource = 'confirmed' | 'manual' | 'estimated';

export type ObjectComparisonFieldKey =
  | 'address'
  | 'purchasePrice'
  | 'livingArea'
  | 'landArea'
  | 'rooms'
  | 'yearBuilt'
  | 'condition'
  | 'parkingSpaces'
  | 'monthlyExpenses'
  | 'expectedRent'
  | 'equity'
  | 'mortgage'
  | 'purchaseCosts';

export interface ObjectComparisonObject {
  id: string;
  name: string;
  address: string;
  purchasePrice: number | null;
  livingArea: number | null;
  landArea: number | null;
  rooms: number | null;
  yearBuilt: number | null;
  condition: string;
  parkingSpaces: number | null;
  monthlyExpenses: number | null;
  expectedRent: number | null;
  equity: number | null;
  mortgage: number | null;
  purchaseCosts: number | null;
  fieldSources: Partial<Record<ObjectComparisonFieldKey, ComparisonDataSource>>;
  source: string;
  dataAsOf: string;
}

export interface ObjectComparisonPayload {
  name: string;
  objects: ObjectComparisonObject[];
}

export interface ObjectComparisonMetrics {
  pricePerSqm: number | null;
  grossYield: number | null;
}

export interface ObjectComparisonFieldDefinition {
  key: ObjectComparisonFieldKey;
  label: string;
  kind: 'text' | 'currency' | 'area' | 'number' | 'year' | 'condition';
  placeholder?: string;
}

export const OBJECT_COMPARISON_FIELD_DEFINITIONS: readonly ObjectComparisonFieldDefinition[] = Object.freeze([
  { key: 'address', label: 'Adresse / Ort', kind: 'text', placeholder: 'z. B. Winterthur' },
  { key: 'purchasePrice', label: 'Kaufpreis', kind: 'currency', placeholder: "1'000'000" },
  { key: 'livingArea', label: 'Wohnfläche', kind: 'area', placeholder: '120' },
  { key: 'landArea', label: 'Grundstücksfläche', kind: 'area', placeholder: '450' },
  { key: 'rooms', label: 'Zimmer', kind: 'number', placeholder: '4.5' },
  { key: 'yearBuilt', label: 'Baujahr', kind: 'year', placeholder: '2012' },
  { key: 'condition', label: 'Zustand', kind: 'condition', placeholder: 'z. B. gepflegt' },
  { key: 'parkingSpaces', label: 'Parkplätze', kind: 'number', placeholder: '2' },
  { key: 'monthlyExpenses', label: 'Monatliche Nebenkosten', kind: 'currency', placeholder: '450' },
  { key: 'expectedRent', label: 'Erwarteter Mietzins / Monat', kind: 'currency', placeholder: '2\'800' },
  { key: 'equity', label: 'Eigenmittel', kind: 'currency', placeholder: "250'000" },
  { key: 'mortgage', label: 'Hypothek / Finanzierung', kind: 'currency', placeholder: "750'000" },
  { key: 'purchaseCosts', label: 'Kaufnebenkosten', kind: 'currency', placeholder: "35'000" },
]);

const numericKeys = new Set<ObjectComparisonFieldKey>([
  'purchasePrice', 'livingArea', 'landArea', 'rooms', 'yearBuilt', 'parkingSpaces',
  'monthlyExpenses', 'expectedRent', 'equity', 'mortgage', 'purchaseCosts',
]);

function parseNumericText(value: unknown, key: ObjectComparisonFieldKey): number | null {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return null;
  if (typeof value !== 'number' && typeof value !== 'string') throw new Error(`«${key}» muss eine Zahl sein oder leer bleiben.`);
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error(`«${key}» enthält keine gültige Zahl.`);
    return value;
  }
  let text = value.trim().replace(/[\u0027\u2019\u2018\u0060\s]/g, '');
  if (!text) return null;
  if ((text.match(/,/g) || []).length > 1) throw new Error(`«${key}» enthält zu viele Dezimaltrennzeichen.`);
  if (text.includes(',')) {
    if ((text.match(/\./g) || []).length > 0) throw new Error(`«${key}» verwendet ein uneindeutiges Zahlenformat.`);
    text = text.replace(',', '.');
  }
  if (!/^-?\d+(?:\.\d+)?$/.test(text)) throw new Error(`«${key}» verwendet ein nicht unterstütztes Zahlenformat.`);
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) throw new Error(`«${key}» enthält keine gültige Zahl.`);
  return parsed;
}

function textValue(value: unknown, key: string, maxLength: number): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') throw new Error(`«${key}» muss ein Text sein.`);
  const result = value.trim();
  if (result.length > maxLength) throw new Error(`«${key}» ist zu lang.`);
  return result;
}

function validateNumber(value: number | null, key: ObjectComparisonFieldKey, minimum: number, maximum: number): number | null {
  if (value === null) return null;
  if (value < minimum || value > maximum) throw new Error(`«${key}» liegt ausserhalb des plausiblen Bereichs.`);
  return value;
}

function normalizeSource(value: unknown, key: string): ComparisonDataSource {
  if (value === undefined || value === null || value === '') return 'manual';
  if (value === 'confirmed' || value === 'manual' || value === 'estimated') return value;
  throw new Error(`Der Datenstatus für «${key}» ist ungültig.`);
}

export function normalizeObjectComparisonPayload(input: unknown): ObjectComparisonPayload {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Der Vergleich konnte nicht gelesen werden.');
  const raw = input as { name?: unknown; objects?: unknown };
  const name = textValue(raw.name, 'Vergleichsname', 120);
  if (name.length < 1) throw new Error('Bitte gib dem Vergleich einen Namen.');
  if (!Array.isArray(raw.objects) || raw.objects.length < 2 || raw.objects.length > 6) throw new Error('Ein Vergleich benötigt zwei bis sechs Objekte.');

  const objects = raw.objects.map((value, index) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`Objekt ${index + 1} ist ungültig.`);
    const rawObject = value as Record<string, unknown>;
    const nameValue = textValue(rawObject.name, `Objekt ${index + 1}`, 120);
    if (nameValue.length < 1) throw new Error(`Bitte gib für Objekt ${index + 1} eine Bezeichnung an.`);
    const fieldSourcesInput = rawObject.fieldSources;
    const fieldSources: Partial<Record<ObjectComparisonFieldKey, ComparisonDataSource>> = {};
    if (fieldSourcesInput !== undefined) {
      if (!fieldSourcesInput || typeof fieldSourcesInput !== 'object' || Array.isArray(fieldSourcesInput)) throw new Error(`Die Datenstatusangaben von Objekt ${index + 1} sind ungültig.`);
      for (const definition of OBJECT_COMPARISON_FIELD_DEFINITIONS) {
        const sourceValue = (fieldSourcesInput as Record<string, unknown>)[definition.key];
        if (sourceValue !== undefined) fieldSources[definition.key] = normalizeSource(sourceValue, definition.key);
      }
    }
    const numberValue = (key: ObjectComparisonFieldKey, minimum: number, maximum: number) => validateNumber(parseNumericText(rawObject[key], key), key, minimum, maximum);
    const year = numberValue('yearBuilt', 1500, new Date().getFullYear() + 2);
    const entry: ObjectComparisonObject = {
      id: textValue(rawObject.id, `Objekt ${index + 1} ID`, 80) || `object-${index + 1}`,
      name: nameValue,
      address: textValue(rawObject.address, 'Adresse / Ort', 240),
      purchasePrice: numberValue('purchasePrice', 0, 2_000_000_000),
      livingArea: numberValue('livingArea', 0, 1_000_000),
      landArea: numberValue('landArea', 0, 10_000_000),
      rooms: numberValue('rooms', 0, 100),
      yearBuilt: year === null ? null : Math.round(year),
      condition: textValue(rawObject.condition, 'Zustand', 120),
      parkingSpaces: numberValue('parkingSpaces', 0, 100),
      monthlyExpenses: numberValue('monthlyExpenses', 0, 10_000_000),
      expectedRent: numberValue('expectedRent', 0, 10_000_000),
      equity: numberValue('equity', 0, 2_000_000_000),
      mortgage: numberValue('mortgage', 0, 2_000_000_000),
      purchaseCosts: numberValue('purchaseCosts', 0, 2_000_000_000),
      fieldSources,
      source: textValue(rawObject.source, 'Quelle', 500),
      dataAsOf: textValue(rawObject.dataAsOf, 'Datenstand', 40),
    };
    if (entry.dataAsOf && !/^\d{4}-\d{2}-\d{2}$/.test(entry.dataAsOf)) throw new Error(`Der Datenstand von Objekt ${index + 1} muss ein Datum sein.`);
    if (entry.livingArea === 0 || entry.landArea === 0 || entry.purchasePrice === 0) {
      // Explicit zero is retained for input fidelity, but it is not enough for
      // derived property metrics. The UI will show the calculation as missing.
    }
    return entry;
  });
  return { name, objects };
}

export function calculateObjectMetrics(object: Pick<ObjectComparisonObject, 'purchasePrice' | 'livingArea' | 'expectedRent'>): ObjectComparisonMetrics {
  const pricePerSqm = object.purchasePrice !== null && object.purchasePrice > 0 && object.livingArea !== null && object.livingArea > 0
    ? object.purchasePrice / object.livingArea
    : null;
  const grossYield = object.purchasePrice !== null && object.purchasePrice > 0 && object.expectedRent !== null && object.expectedRent > 0
    ? (object.expectedRent * 12 / object.purchasePrice) * 100
    : null;
  return { pricePerSqm: Number.isFinite(pricePerSqm) ? pricePerSqm : null, grossYield: Number.isFinite(grossYield) ? grossYield : null };
}

export function formatSwissNumber(value: number, maximumFractionDigits = 2): string {
  if (!Number.isFinite(value)) return 'unbekannt';
  return new Intl.NumberFormat('de-CH', { maximumFractionDigits, useGrouping: true }).format(value).replace(/\u00a0/g, ' ');
}

export function formatSwissCurrency(value: number | null): string {
  return value === null || !Number.isFinite(value) ? 'nicht vorhanden' : `CHF ${formatSwissNumber(value)}`;
}

export function formatMetric(value: number | null, unit: string, digits = 2): string {
  return value === null || !Number.isFinite(value) ? 'Nicht berechnet – Angaben fehlen' : `${formatSwissNumber(value, digits)}${unit}`;
}

export function sourceLabel(value: ComparisonDataSource | undefined): string {
  return value === 'confirmed' ? 'bestätigt' : value === 'estimated' ? 'geschätzt' : 'manuell';
}

export function isNumericComparisonField(key: ObjectComparisonFieldKey): boolean { return numericKeys.has(key); }
